import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiErrorText, apiRequest } from '../../utils';
import './CommsStats.css';

/* What the shelf has come to: how full it is, how often it has been played, and what each house
   has won.

   ★★ TWO WAYS OF SCORING THE GAMES, never added, and the screen leads with ONE of them. The
   house cup scores a day on PLACE and is what a company day is meant to be read off; the classic
   total is the plain sum of the houses' team totals. They score the same games, so summing them
   would count every point twice. Whenever any day has been converted (cup_days) the cup leads
   and the raw sum is said beside it; with no cup anywhere the screen is exactly what it was.

   ★★ AND A THIRD SOURCE THAT DOES ADD: `earned`, what a house's people brought in through the
   engagement weights (/engagement/rules). It is not a game, so it double-counts neither of the
   two above. `total` is the board — the leading game figure plus this — and the server works it
   out, because it is the only place that knows which game figure leads.

   ★★ Within each, the points are the STANDING — not a sum of the awards ledger, which is a slice
   of those totals; adding the two would count every hand award twice. The invariant is written
   out in shared_models/queries/house_points.py.

   ⚠️ THE FAN-OUT IS SAID OUT LOUD, under the tiles, and that line is not decoration. A house
   total is the plain sum of its TEAMS' totals, so 50 points awarded to a house of six teams reads
   as 300 here. Every leaderboard on record was scored that way. An admin who reads "1 800" with
   no explanation and then counts the awards by hand will conclude the screen is broken.

   ⚠️ "Days played" counts days a house was PUT ON an event, and a cup needs no such row — it
   reaches a house through the people who played. So a converted day can sit beside "0 days
   played", and the cup has its own count rather than being folded into that one. */

const Tile = ({ n, label, tone }) => (
  <div className={`cst-tile ${tone || ''}`}>
    <span className="cst-tile__n">{n}</span>
    <span className="cst-tile__label">{label}</span>
  </div>
);

const HouseStats = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setData(null);
    setError(null);
    apiRequest('GET', '/company/houses/stats')
      .then(setData)
      .catch((err) => setError(apiErrorText(t, err)));
  }, [t]);

  useEffect(() => { load(); }, [load]);

  if (error) return <p className="bravos-cal__notice">{error}</p>;
  if (data === null) return <span className="auth__spinner" />;

  const rows = data.rows || [];
  if (rows.length === 0) return <p className="ce-note">{t('statsNoHousesYet')}</p>;

  // A cup exists the moment any day has been converted. Not "are there cup points" — a day whose
  // ranking was all zeros converts to a standing of zeros, and that is still the cup.
  const hasCup = (data.cup_days || 0) > 0;
  // The off-the-field half: what the houses' people brought in through the engagement weights.
  // Zero for every company that has not set one, which is what keeps this invisible.
  const hasEarned = (data.earned_points || 0) > 0;
  // ★ `total_points` is the board: whichever game figure this company scores by, plus what was
  // earned. Added here and not on the screen — the server is the one place that knows which of
  // the two game figures leads, and a second opinion is a second answer to "who is first".
  const lead = hasCup || hasEarned ? data.total_points : data.points;

  return (
    <div className="cst">
      <div className="cst-tiles">
        <Tile n={data.houses} label={t('statsHouses')} />
        <Tile n={`${data.assigned}/${data.roster}`} label={t('statsInAHouse')}
              tone={data.unplaced ? '' : 'is-good'} />
        {data.unplaced > 0 && (
          <Tile n={data.unplaced} label={t('statsNotPlacedYet')} tone="is-warn" />
        )}
        <Tile n={hasCup ? data.cup_days : data.days_played}
              label={hasCup ? t('statsCupDays') : t('statsHouseDays')} />
        {/* One number, because that is what a standing is. The cup label only survives while the
            cup is ALL of it; the moment engagement pays in too, calling the sum "cup points"
            would name it after half of itself. */}
        <Tile n={lead}
              label={hasCup && !hasEarned ? t('statsCupPoints') : t('statsHousePoints')} />
      </div>

      {/* The balance as its two ends rather than as a verdict: "even enough" needs a threshold
          nobody has agreed on, and the two numbers let an admin decide for themselves. Only when
          there is more than one house — one house is never unbalanced. */}
      {data.houses > 1 && data.biggest !== data.smallest && (
        <p className="ce-note">
          {t('statsHouseBalance', { biggest: data.biggest, smallest: data.smallest })}
        </p>
      )}
      {/* ★ The raw sum is never hidden when the cup leads — it is the number every older
          leaderboard was read off, and an admin who remembers it has to be able to find it
          rather than conclude the screen lost it. */}
      {hasCup || hasEarned ? (
        <>
          {hasCup && <p className="ce-note">{t('statsCupNote')}</p>}
          {hasEarned && (
            <p className="ce-note">{t('statsEarnedNote', { n: data.earned_points })}</p>
          )}
          {/* Only when there is a sum to compare with: "would come to 0" explains nothing. */}
          {hasCup && data.points > 0 && (
            <p className="ce-note">{t('statsCupRawAside', { n: data.points })}</p>
          )}
        </>
      ) : (
        <>
          {data.days_played === 0
            ? <p className="ce-note">{t('statsHousesNoDays')}</p>
            : <p className="ce-note">{t('statsHousePointsNote')}</p>}
          {data.points_capped && <p className="ce-note">{t('statsHousePointsCapped')}</p>}
        </>
      )}

      <div className="cst-breakdown mt-2">
        {rows.map((row) => (
          <div key={row.id} className="cst-line">
            <span className="cst-line__label">
              {/* The colour IS the house — chosen per row by the company, so inline. */}
              <span className="cst-line__swatch"
                    style={{ backgroundColor: row.color || '#5a6472',
                             backgroundImage: row.image ? `url("${row.image}")` : undefined }} />
              {row.name}
            </span>
            <span className="cst-line__sub">
              {[t('statsNMembers', { count: row.members }),
                row.days ? t('statsNDays', { count: row.days }) : null,
                // The split, and only the halves that say something. "0 from team totals" on
                // every line is noise, and the asides above already give the company-wide
                // figures.
                hasCup && row.points ? t('statsRawPoints', { n: row.points }) : null,
                hasEarned && row.earned
                  ? t('statsEarnedPoints', { n: row.earned }) : null]
                .filter(Boolean).join(' · ')}
            </span>
            <span className="cst-line__n">
              {hasCup || hasEarned ? row.total : row.points}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HouseStats;
